/**
 * Derive the current page index from a WordPress-style URL path.
 * Supports `/.../page/{n}/` or `/.../page/{n}`; defaults to 1 when absent or invalid.
 */
export const getPageFromLocation = (location: Location): number => {
  const match = location.pathname.match(/\/page\/(\d+)\/?$/)
  if (!match) {
    return 1
  }

  const page = Number(match[1])
  if (!Number.isFinite(page) || page < 1) {
    return 1
  }

  return page
}

interface PaginationStoreStrategy {
  getInitialPage(): number
  setPage(page: number): void
}

/**
 * Pagination store strategy that encodes the current page as a WordPress-style
 * `/page/{n}/` path segment, while keeping filters/search as query params.
 */
export class WordPressPathPaginationStrategy implements PaginationStoreStrategy {
  getInitialPage(): number {
    if (typeof window === 'undefined') {
      return 1
    }

    return getPageFromLocation(window.location)
  }

  setPage(page: number): void {
    if (typeof window === 'undefined') {
      return
    }

    const { history, location } = window

    const currentPage = getPageFromLocation(location)
    if (page === currentPage) {
      return
    }

    const currentPathname = location.pathname
    const basePath = currentPathname.replace(/\/page\/\d+\/?$/, '')

    const normalizedBase =
      basePath === '' ? '/' : basePath.endsWith('/') ? basePath.slice(0, -1) : basePath

    const nextPath =
      page <= 1 ? normalizedBase : `${normalizedBase === '/' ? '' : normalizedBase}/page/${page}/`

    const nextUrl = `${nextPath}${location.search}${location.hash}`
    const currentUrl = `${currentPathname}${location.search}${location.hash}`

    if (nextUrl === currentUrl) {
      return
    }

    history.replaceState(history.state, '', nextUrl)
  }
}
